import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, Play } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { SELECTED_WORK } from "@/lib/pulseConstants";
import { Button } from "@/components/ui/button";
import { VideoModal } from "@/components/shared/VideoModal";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";

/**
 * Selected Work — three large editorial case-study rows, alternating image side.
 * Interaction:
 *  - Image cards: hover swaps to secondary image (cross-fade + slight zoom).
 *  - Video cards (item.video present): reveals a "WATCH FILM" affordance that
 *    opens an accessible video modal. No autoplay. Poster only until played.
 * Falls back to first image on touch devices — full gallery on the detail page.
 */
export default function SelectedWorkSection() {
    const items = SELECTED_WORK.filter((c) => c.revealed);
    const [videoItem, setVideoItem] = useState(null);

    if (items.length === 0) return null;

    return (
        <section
            id="selected-work"
            className="section-pad relative scroll-mt-24 hidden md:block"
            data-testid="selected-work-section"
        >
            <div className="container-editorial">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div>
                        <SectionEyebrow index="02" label="Selected Work" />
                        <Reveal>
                            <h2 className="display-2 text-white text-balance">
                                Proof before <span className="text-[#FF2A2A]">promise.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <p className="body-md max-w-md">
                            Real brand experiences designed to earn attention, participation and a life beyond the event.
                        </p>
                    </Reveal>
                </div>

                <div className="space-y-16 md:space-y-24">
                    {items.map((item, i) => (
                        <CaseStudyRow
                            key={item.key}
                            item={item}
                            index={i}
                            onOpenVideo={() => setVideoItem(item)}
                        />
                    ))}
                </div>

                {/* Section footer CTA */}
                <Reveal>
                    <div
                        className="mt-20 md:mt-28 rounded-2xl border border-white/10 bg-gradient-to-br from-[#0B0C10] via-[#0B0C10] to-[#170608] p-8 md:p-14 text-center relative overflow-hidden"
                        data-testid="selected-work-footer-cta"
                    >
                        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-[#FF2A2A]/10 blur-3xl pointer-events-none" aria-hidden="true" />
                        <div className="relative">
                            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
                                Next Up
                            </div>
                            <h3 className="display-2 text-white text-balance">
                                Your brand could be <span className="text-[#FF2A2A]">next</span>.
                            </h3>
                            <p className="body-lg mt-5 max-w-2xl mx-auto">
                                Bring us the brief. We’ll help turn it into an experience people want to attend,
                                share and remember.
                            </p>
                            <div className="mt-8 flex justify-center">
                                <Button
                                    asChild
                                    className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12 md:h-14 px-7 md:px-8 text-base"
                                    data-testid="selected-work-cta"
                                >
                                    <Link to="/contact" onClick={handlePulseBriefClick} className="inline-flex items-center gap-2">
                                        Start Your Pulse Brief <ArrowUpRight size={18} />
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>

            {/* Video modal — mounts only when a video item is selected */}
            <VideoModal
                open={Boolean(videoItem)}
                onOpenChange={(v) => { if (!v) setVideoItem(null); }}
                title={videoItem ? videoItem.title : ""}
                description={videoItem ? videoItem.summary : ""}
                poster={videoItem && videoItem.video ? videoItem.video.poster : ""}
                posterAlt={videoItem && videoItem.video ? videoItem.video.posterAlt : ""}
                sources={videoItem && videoItem.video ? videoItem.video.sources : []}
                videoId={videoItem ? videoItem.slug : ""}
                placement="selected_work"
            />
        </section>
    );
}

function CaseStudyRow({ item, index, onOpenVideo }) {
    const [hover, setHover] = useState(false);
    const imageRight = item.layout === "image-right";
    const hasVideo = Boolean(item.video && item.video.sources && item.video.sources.length);

    const imageBody = (
        <div
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B0C10] shadow-[0_30px_60px_-30px_rgba(255,42,42,0.15)]"
            style={{ aspectRatio: item.primary_image_aspect }}
        >
            {/* Primary image (default state) */}
            <motion.img
                src={item.primary_image}
                alt={item.primary_image_alt}
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "auto"}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: item.primary_image_position || "center" }}
                initial={{ scale: 1 }}
                animate={{ scale: hover ? 1.03 : 1 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            />
            {/* Secondary image cross-fade on hover (image projects only) */}
            {!hasVideo && (
                <AnimatePresence>
                    {hover && (
                        <motion.img
                            key="secondary"
                            src={item.secondary_image}
                            alt={item.secondary_image_alt}
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ objectPosition: item.secondary_image_position || "center" }}
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1.03 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        />
                    )}
                </AnimatePresence>
            )}

            {/* Gradient wash for legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/10 pointer-events-none" />

            {/* Project number — huge outlined type */}
            <div className="absolute top-5 left-5 md:top-6 md:left-6">
                <span className="font-display text-5xl md:text-6xl lg:text-7xl leading-none text-white/95 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">
                    {item.number}
                </span>
            </div>

            {/* Video: restrained play affordance centered */}
            {hasVideo && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <motion.div
                        initial={{ opacity: 0.9, scale: 1 }}
                        animate={{ opacity: hover ? 1 : 0.9, scale: hover ? 1.06 : 1 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="h-16 w-16 md:h-20 md:w-20 rounded-full border border-white/40 bg-black/45 backdrop-blur flex items-center justify-center text-white shadow-[0_10px_40px_-10px_rgba(255,42,42,0.6)]"
                        aria-hidden="true"
                    >
                        <Play size={26} className="ml-1" />
                    </motion.div>
                </div>
            )}

            {/* Bottom-right hint pill */}
            <div className="absolute bottom-5 right-5 md:bottom-6 md:right-6">
                <motion.div
                    initial={{ opacity: 0.85 }}
                    animate={{ opacity: hover ? 1 : 0.85 }}
                    className="inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur border border-white/20 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-white"
                >
                    <span>{hasVideo ? "Watch Film" : "Hover to preview"}</span>
                    <ArrowRight size={12} />
                </motion.div>
            </div>
        </div>
    );

    const imageCol = (
        <div
            className={
                `relative order-1 ${imageRight ? "lg:order-2 lg:col-span-7" : "lg:col-span-7"}`
            }
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {hasVideo ? (
                <button
                    type="button"
                    onClick={onOpenVideo}
                    aria-label={`Watch the ${item.title} film`}
                    className="block w-full text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-4 focus-visible:ring-offset-black rounded-2xl"
                    data-testid={`case-study-video-trigger-${item.slug}`}
                >
                    {imageBody}
                </button>
            ) : (
                <Link
                    to={`/work/${item.slug}`}
                    aria-label={`View ${item.title} project details`}
                    className="block group"
                    data-testid={`case-study-image-link-${item.slug}`}
                >
                    {imageBody}
                </Link>
            )}
        </div>
    );

    const contentCol = (
        <div className={`order-2 ${imageRight ? "lg:order-1 lg:col-span-5" : "lg:col-span-5"} flex flex-col justify-center`}>
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-3">
                {item.client}
            </div>
            <Reveal delay={0.05}>
                <h3 className="display-3 text-white leading-[0.95]">{item.title}</h3>
            </Reveal>
            <Reveal delay={0.1}>
                <div className="flex flex-wrap gap-2 mt-5">
                    {item.categories.map((cat) => (
                        <span
                            key={cat}
                            className="px-2.5 py-1 rounded-full border border-white/15 text-[10px] font-mono text-white/75 uppercase tracking-[0.18em]"
                        >
                            {cat}
                        </span>
                    ))}
                </div>
            </Reveal>
            <Reveal delay={0.15}>
                <p className="body-lg mt-6">{item.summary}</p>
            </Reveal>
            <Reveal delay={0.2}>
                <p className="body-md mt-4 max-w-xl">{item.supporting_copy}</p>
            </Reveal>
            <Reveal delay={0.25}>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                    {hasVideo ? (
                        <>
                            <button
                                type="button"
                                onClick={onOpenVideo}
                                data-testid={`case-study-cta-${item.slug}`}
                                className="group inline-flex items-center gap-3 text-white font-semibold focus-visible:outline-none min-h-11 py-2"
                            >
                                <span className="relative pb-1">
                                    Watch Film
                                    <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-[#FF2A2A] origin-left scale-x-100 transition-transform" />
                                </span>
                                <span className="h-11 w-11 rounded-full border border-white/20 flex items-center justify-center text-white/80 group-hover:bg-[#FF2A2A] group-hover:border-[#FF2A2A] group-hover:text-black transition-colors">
                                    <Play size={14} className="ml-0.5" />
                                </span>
                            </button>
                            <Link
                                to={`/work/${item.slug}`}
                                data-testid={`case-study-detail-link-${item.slug}`}
                                className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors min-h-11 py-3"
                            >
                                View Project <ArrowUpRight size={14} />
                            </Link>
                        </>
                    ) : (
                        <Link
                            to={`/work/${item.slug}`}
                            data-testid={`case-study-cta-${item.slug}`}
                            className="group inline-flex items-center gap-3 text-white font-semibold min-h-11 py-2"
                        >
                            <span className="relative pb-1">
                                View Project
                                <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-[#FF2A2A] origin-left scale-x-100 group-hover:scale-x-100 transition-transform" />
                            </span>
                            <span className="h-11 w-11 rounded-full border border-white/20 flex items-center justify-center text-white/80 group-hover:bg-[#FF2A2A] group-hover:border-[#FF2A2A] group-hover:text-black transition-colors">
                                <ArrowUpRight size={14} />
                            </span>
                        </Link>
                    )}
                </div>
            </Reveal>
        </div>
    );

    return (
        <Reveal delay={0.04 * index}>
            <article
                data-testid={`case-study-row-${item.slug}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 lg:gap-14 items-center"
            >
                {imageCol}
                {contentCol}
            </article>
        </Reveal>
    );
}
